import { useCallback, useContext, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import WavesIcon from "@mui/icons-material/Waves";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BookmarkBorderRoundedIcon from "@mui/icons-material/BookmarkBorderRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import { TitleContext } from "../components/Context";
import {
  FeedCandidate,
  FeedConfidence,
  FeedbackAction,
  FeedItem,
  FeedResponse,
  PublicEnvironmentalPublication,
  getLatestEnvironmentalFeed,
  getEnvironmentalFeedHistory,
  getPublicEnvironmentalPublication,
  getEnvironmentalPublicationShareUrl,
  sendEnvironmentalFeedFeedback,
} from "../services/environmentalFeedApi";
import {
  loadSavedEnvironmentalSignalIds,
  persistSavedEnvironmentalSignalIds,
} from "../services/environmentalFeedSaved";
import { useResponsiveLayout } from "../hooks/useResponsiveLayout";
import classes from "./EnvironmentalFeedPage.module.css";
import PartnerLogos from "../components/UI/PartnerLogos";
import { trackAnalyticsEvent } from "../services/analytics";

const kindLabels: Record<string, string> = {
  observation: "Observación",
  anomaly: "Desvío histórico",
  data_quality: "Calidad de datos",
  beach_ranking: "Ranking microbiológico",
  reflect_bead: "Resumen reflexivo",
};

const kindIcons: Record<string, JSX.Element> = {
  observation: <WavesIcon fontSize="small" />,
  anomaly: <WarningAmberOutlinedIcon fontSize="small" />,
  data_quality: <AccessTimeOutlinedIcon fontSize="small" />,
  beach_ranking: <EmojiEventsOutlinedIcon fontSize="small" />,
  reflect_bead: <QueryStatsOutlinedIcon fontSize="small" />,
};

const confidenceLabels: Record<FeedConfidence, string> = {
  high: "Alta confianza",
  medium: "Confianza media",
  low: "Baja confianza",
  unknown: "Confianza no determinada",
};

export function formatDate(value: string | null | undefined) {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "Fecha no disponible";
  return new Intl.DateTimeFormat("es-UY", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Montevideo",
  }).format(date);
}

function compactText(value: string, maxLength: number) {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > maxLength
    ? `${normalized.slice(0, maxLength - 1).trimEnd()}…`
    : normalized;
}

const anomalyAttributeLabels: Record<string, string> = {
  temperatura: "Temperatura",
  temperatura_agua: "Temperatura del agua",
  salinidad: "Salinidad",
  nivel_mar: "Nivel del mar",
};

const anomalyLocationLabels: Record<string, string> = {
  ramirez: "Ramírez",
  del_cerro: "Del Cerro",
  pocitos: "Pocitos",
  de_los_cilindros: "De los Cilindros",
  simcosta_rs4: "SimCOSTA RS-4",
  exo_isla_flores: "EXO Isla de Flores",
  anp_muelle_fluvial: "ANP Muelle Fluvial",
};

function formatLocation(value: string) {
  return anomalyLocationLabels[value] || value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatAnomalyValue(value: unknown) {
  return typeof value === "number"
    ? value.toLocaleString("es-UY", { maximumFractionDigits: 3 })
    : "—";
}

function anomalyRows(candidate: FeedCandidate) {
  const raw = candidate.facts?.anomalies;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object"));
}

function microbiologyRows(candidate: FeedCandidate) {
  const raw = candidate.facts?.aptitude_by_beach;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  return Object.entries(raw as Record<string, unknown>)
    .filter(([, value]) => Boolean(value && typeof value === "object" && (value as Record<string, unknown>).overall_status === "fail"))
    .map(([beach, value]) => {
      const details = value as Record<string, unknown>;
      const failedClasses = ["class_3", "class_2b"]
        .map((name) => ({ name, value: details[name] }))
        .filter(({ value }) => Boolean(value && typeof value === "object" && (value as Record<string, unknown>).status === "fail"));
      const firstClass = failedClasses[0]?.value as Record<string, unknown> | undefined;
      const classNames = failedClasses.map(({ name }) => name === "class_3" ? "Clase 3" : "Clase 2b");
      return {
        beach,
        details: `máx. cf ${formatAnomalyValue(firstClass?.max_cf_100ml)} · media geométrica ${formatAnomalyValue(firstClass?.geomean_cf_100ml)} · ${classNames.join(" y ")} fuera`,
      };
    });
}

function historicalObservationRows(candidate: FeedCandidate) {
  const raw = candidate.facts?.observations;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object"))
    .sort((a, b) => Number(b.role === "mareografica") - Number(a.role === "mareografica"));
}

function beachRankingRows(candidate: FeedCandidate) {
  const raw = candidate.facts?.ranking;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object"));
}

function currentColiformRows(candidate: FeedCandidate) {
  const raw = candidate.facts?.current_samples;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object"));
}

type EvidenceMode = "comparison" | "samples" | "aggregates" | "ranking" | "quality";

type EvidencePoint = {
  id: string;
  label: string;
  location?: string;
  value?: number;
  reference?: number;
  current?: number;
  measuredAt?: string;
  receivedAt?: string;
  windowStart?: string;
  windowEnd?: string;
  sampleCount?: number;
  unit?: string;
  isAnomaly?: boolean;
  sourceTable?: string;
  qualityStatus?: string;
  sampleAgeHours?: number;
  receivedAgeHours?: number;
};

type EvidencePanel = {
  key: string;
  title: string;
  unit?: string;
  mode: EvidenceMode;
  points: EvidencePoint[];
  totalCount?: number;
  explanation?: string;
  asOf?: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function recordEntries(value: unknown): Array<[string, Record<string, unknown>]> {
  const record = asRecord(value);
  if (!record) return [];
  return Object.entries(record)
    .map(([key, nested]) => [key, asRecord(nested)] as const)
    .filter((entry): entry is [string, Record<string, unknown>] => entry[1] !== null);
}

function stringValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value : undefined;
}


function finiteNumber(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function formatChartValue(value: number) {
  return value.toLocaleString("es-UY", { maximumFractionDigits: 2 });
}

function evolutionDateLabel(value: unknown, fallback: string) {
  const date = new Date(String(value || ""));
  return Number.isNaN(date.getTime())
    ? fallback
    : new Intl.DateTimeFormat("es-UY", { day: "2-digit", month: "2-digit", timeZone: "America/Montevideo" }).format(date);
}
function parameterLabel(parameter: string) {
  const labels: Record<string, string> = {
    coliformes_termotolerantes: "Coliformes termotolerantes · muestra directa",
    enterococos_ecocos: "Enterococos · muestra directa",
    enterococos_mg5: "Enterococos · media geométrica de 5",
    nivel_mar: "Nivel del mar",
    salinidad: "Salinidad",
    temperatura: "Temperatura",
    turbidez: "Turbidez",
  };
  return labels[parameter] || formatLocation(parameter);
}

function parameterValue(row: Record<string, unknown>, parameter: string) {
  const keys: Record<string, string[]> = {
    coliformes_termotolerantes: ["value_cf_100ml", "coliformes_termotolerantes"],
    enterococos_ecocos: ["enterococos_ecocos"],
    enterococos_mg5: ["enterococos_mg5", "value_mg5_100ml"],
  };
  return keys[parameter]?.map((key) => finiteNumber(row[key])).find((value): value is number => value !== null) ?? null;
}

function evidenceWindow(point: EvidencePoint) {
  if (!point.windowStart && !point.windowEnd) return "Fecha puntual";
  return `${point.windowStart ? evolutionDateLabel(point.windowStart, "—") : "—"} a ${point.windowEnd ? evolutionDateLabel(point.windowEnd, "—") : "—"}`;
}

function windowDays(point: EvidencePoint) {
  if (!point.windowStart || !point.windowEnd) return null;
  const start = Date.parse(point.windowStart);
  const end = Date.parse(point.windowEnd);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  return Math.round((end - start) / 86400000) + 1;
}

function microbiologySourceRows(candidate: FeedCandidate, parameter: string) {
  const facts = candidate.facts || {};
  const currentSamples = Array.isArray(facts.current_samples)
    ? facts.current_samples
      .map((item) => asRecord(item))
      .filter((item): item is Record<string, unknown> => item !== null)
      .map((row) => ({ row, value: parameterValue(row, parameter) }))
      .filter((item): item is { row: Record<string, unknown>; value: number } => item.value !== null)
    : [];
  if (currentSamples.length > 0) return currentSamples;

  const topValues = asRecord(facts.top_values);
  const rows = topValues?.[parameter === "coliformes_termotolerantes" ? "cf" : parameter === "enterococos_ecocos" ? "ecocos" : "mg5"];
  return (Array.isArray(rows) ? rows : [])
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((row) => ({ row, value: parameterValue(row, parameter) }))
    .filter((item): item is { row: Record<string, unknown>; value: number } => item.value !== null);
}

function buildComparisonPanels(candidate: FeedCandidate, rows: Record<string, unknown>[]): EvidencePanel[] {
  const groups = new Map<string, EvidencePoint[]>();
  rows.forEach((row, index) => {
    const attribute = String(row.attribute || "Indicador");
    const unit = stringValue(row.unit);
    const key = `${attribute}|${unit || ""}`;
    const points = groups.get(key) || [];
    const reference = finiteNumber(row.baseline_median);
    const current = finiteNumber(row.recent_median);
    if (reference === null && current === null) return;
    points.push({
      id: `${key}-${index}`,
      label: formatLocation(String(row.source || row.beach || `Punto ${index + 1}`)),
      reference: reference ?? undefined,
      current: current ?? undefined,
      unit,
      isAnomaly: row.is_anomaly === true || candidate.kind === "anomaly",
      sampleCount: finiteNumber(row.recent_samples) ?? undefined,
      windowStart: stringValue(row.recent_start),
      windowEnd: stringValue(row.recent_end),
    });
    groups.set(key, points);
  });

  return Array.from(groups.entries()).map(([key, points]) => {
    const [attribute, unit] = key.split("|");
    return {
      key,
      title: parameterLabel(attribute),
      unit: unit || undefined,
      mode: "comparison",
      points,
      explanation: "Comparación del período reciente frente a la referencia histórica del mismo indicador y estación.",
    };
  });
}

function buildMicrobiologyPanels(candidate: FeedCandidate): EvidencePanel[] {
  const facts = candidate.facts || {};
  const microbiologyFacts = asRecord(facts.microbiology);
  const parameters = Array.isArray(facts.parameters)
    ? facts.parameters.filter((value): value is string => typeof value === "string")
    : ["coliformes_termotolerantes", "enterococos_ecocos", "enterococos_mg5"];
  const recentCounts = asRecord(facts.recent_counts ?? microbiologyFacts?.recent_counts);

  return parameters.flatMap((parameter) => {
    const rows = microbiologySourceRows(candidate, parameter);
    if (rows.length === 0) return [];
    const isAggregate = parameter === "enterococos_mg5";
    const points = rows
      .map(({ row, value }, index) => ({
        id: `${parameter}-${String(row.beach || row.playa || "punto")}-${String(row.timestamp || index)}`,
        label: formatLocation(String(row.beach || row.playa || row.lugar_muestreo || `Punto ${index + 1}`)),
        location: String(row.beach || row.playa || row.lugar_muestreo || `Punto ${index + 1}`),
        value,
        measuredAt: stringValue(row.timestamp),
        windowStart: stringValue(row.window_start),
        windowEnd: stringValue(row.window_end),
        sampleCount: finiteNumber(row.aggregate_sample_count) ?? undefined,
        unit: "NMP/100 ml",
        sourceTable: stringValue(row.source_table),
      }))
      .sort((a, b) => Date.parse(a.measuredAt || a.windowEnd || "") - Date.parse(b.measuredAt || b.windowEnd || ""));

    return [{
      key: parameter,
      title: parameterLabel(parameter),
      unit: "NMP/100 ml",
      mode: isAggregate ? "aggregates" : "samples",
      points,
      totalCount: finiteNumber(recentCounts?.[parameter]) ?? points.length,
      explanation: isAggregate
        ? "MG5 resume cinco muestras mediante una media geométrica. Cada punto representa una ventana, no una muestra tomada en la fecha de publicación."
        : "Las muestras directas se mantienen separadas por estación y se ordenan por la fecha de medición.",
    }];
  });
}

function buildRankingPanel(candidate: FeedCandidate): EvidencePanel[] {
  const rows = beachRankingRows(candidate);
  const points: EvidencePoint[] = [];
  rows.forEach((row, index) => {
    const value = finiteNumber(row.value_mg5_100ml)
      ?? finiteNumber(row.geomean_cf_100ml)
      ?? finiteNumber(row.max_cf_100ml);
    if (value === null) return;
    points.push({
      id: `${String(row.beach || "estación")}-${index}`,
      label: formatLocation(String(row.beach || `Estación ${index + 1}`)),
      value,
      unit: "NMP/100 ml",
      windowStart: stringValue(row.window_start),
      windowEnd: stringValue(row.window_end),
      measuredAt: stringValue(row.timestamp_fecha_datos),
      sampleCount: finiteNumber(row.aggregate_sample_count) ?? finiteNumber(row.sample_count) ?? undefined,
      sourceTable: stringValue(row.source_table),
    });
  });
  if (points.length === 0) return [];

  const durations = points.map(windowDays).filter((value): value is number => value !== null);
  const unequalWindows = new Set(durations).size > 1;
  return [{
    key: "ranking",
    title: `Comparación descriptiva · ${candidate.facts?.ranking_parameter === "enterococos_mg5" ? "Enterococos MG5" : "microbiología"}`,
    unit: "NMP/100 ml",
    mode: "ranking",
    points,
    explanation: `${unequalWindows ? "Las ventanas no tienen la misma duración; el orden es descriptivo." : "Cada punto muestra un valor agregado por estación."} La ventana se conserva en cada fila.`,
  }];
}

function buildQualityPanel(candidate: FeedCandidate): EvidencePanel[] {
  const facts = candidate.facts || {};
  const sourceStatus = recordEntries(facts.source_status);
  const parameterSummary = recordEntries(facts.parameter_summary);
  const sourcePoints = sourceStatus.map(([source, details]) => {
    const parameter = stringValue(details.parameter || details.attribute);
    const hasQualityFlags = Object.keys(asRecord(details.quality_flags) || {}).length > 0;
    return {
      id: `${source}:${parameter || ""}:${String(details.source || "")}`,
      label: `${formatLocation(source.replace(/^(beach:)/, ""))}${parameter ? ` · ${parameterLabel(parameter)}` : ""}`,
      qualityStatus: hasQualityFlags ? "flagged" : String(details.status || "unknown"),
      measuredAt: stringValue(details.timestamp),
      receivedAt: stringValue(details.recvTime),
      sampleAgeHours: finiteNumber(details.sample_age_hours) ?? undefined,
      receivedAgeHours: finiteNumber(details.received_age_hours) ?? undefined,
      sourceTable: stringValue(details.source),
    };
  });
  const parameterPoints = parameterSummary.map(([parameter, details]) => ({
    id: parameter,
    label: parameterLabel(parameter),
    qualityStatus: String(details.freshness_status || "unknown"),
    measuredAt: stringValue(details.latest_sample_at),
    receivedAt: stringValue(details.latest_received_at),
    sampleAgeHours: finiteNumber(details.freshness_hours) ?? undefined,
    sourceTable: stringValue(details.source_table),
  }));
  const points = sourcePoints.length > 0 ? sourcePoints : parameterPoints;
  const flagged = Array.isArray(facts.quality_flagged_sources) ? facts.quality_flagged_sources.length : 0;
  if (points.length === 0 && flagged === 0) return [];
  return [{
    key: "quality",
    title: "Cobertura y calidad por fuente",
    mode: "quality",
    points,
    totalCount: points.length,
    asOf: candidate.published_at,
    explanation: flagged > 0
      ? `${flagged} fuente${flagged === 1 ? "" : "s"} marcada${flagged === 1 ? "" : "s"} por plausibilidad. El valor no se oculta: se identifica como dato a revisar.`
      : "La tabla distingue disponibilidad, antigüedad de la medición y antigüedad de recepción.",
  }];
}

export function buildEvidencePanels(candidate: FeedCandidate): EvidencePanel[] {
  if (candidate.kind === "anomaly") return buildComparisonPanels(candidate, anomalyRows(candidate));
  if (candidate.kind === "observation") {
    const comparison = buildComparisonPanels(candidate, historicalObservationRows(candidate));
    const microbiology = buildMicrobiologyPanels(candidate);
    return comparison.length > 0 ? comparison : microbiology;
  }
  if (candidate.kind === "beach_ranking") return buildRankingPanel(candidate);
  if (candidate.kind === "data_quality") return buildQualityPanel(candidate);
  return [];
}

function scaleValues(panel: EvidencePanel) {
  return panel.points.flatMap((point) =>
    [point.reference, point.current, point.value].filter((value): value is number => value !== undefined && Number.isFinite(value))
  );
}

function scaleValue(value: number, min: number, max: number, mode: "linear" | "logarithmic") {
  if (mode === "logarithmic") {
    const safeMin = Math.max(min, Number.MIN_VALUE);
    return (Math.log10(Math.max(value, safeMin)) - Math.log10(safeMin))
      / Math.max(Math.log10(Math.max(max, safeMin)) - Math.log10(safeMin), 1);
  }
  return (value - min) / Math.max(max - min, 1);
}

function axisTicks(min: number, max: number, mode: "linear" | "logarithmic") {
  if (mode === "logarithmic") {
    const start = Math.ceil(Math.log10(Math.max(min, Number.MIN_VALUE)));
    const end = Math.floor(Math.log10(Math.max(max, Number.MIN_VALUE)));
    return Array.from({ length: Math.max(end - start + 1, 1) }, (_, index) => 10 ** (start + index));
  }
  return [min, min + (max - min) / 2, max];
}

function FindingEvidencePanel({ panel, sourceRef }: { panel: EvidencePanel; sourceRef?: string }) {
  const values = scaleValues(panel);
  const positiveValues = values.filter((value) => value > 0);
  const canLogarithmic = positiveValues.length === values.length && values.length > 0 && Math.max(...values) / Math.min(...values) >= 10;
  const [scaleMode, setScaleMode] = useState<"linear" | "logarithmic">("linear");
  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);
  useEffect(() => {
    const clearSelection = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setSelectedPointId(null);
    };
    window.addEventListener("keydown", clearSelection);
    return () => window.removeEventListener("keydown", clearSelection);
  }, []);
  const isHorizontal = panel.mode === "ranking" || panel.mode === "aggregates";
  const min = scaleMode === "logarithmic"
    ? Math.min(...positiveValues)
    : isHorizontal
      ? 0
      : Math.min(...values);
  const max = Math.max(...values);
  const plotLeft = isHorizontal ? 136 : 48;
  const plotRight = 548;
  const plotTop = 24;
  const plotBottom = isHorizontal
    ? 24 + panel.points.length * 32
    : panel.mode === "samples" ? 182 : 156;
  const chartHeight = isHorizontal ? Math.max(210, plotBottom + 18) : panel.mode === "samples" ? 260 : 210;
  const xFor = (index: number) => panel.points.length === 1
    ? (plotLeft + plotRight) / 2
    : plotLeft + (index / (panel.points.length - 1)) * (plotRight - plotLeft);
  const xForValue = (value: number) => plotLeft + scaleValue(value, min, max, scaleMode) * (plotRight - plotLeft);
  const yFor = (value: number) => plotBottom - scaleValue(value, min, max, scaleMode) * (plotBottom - plotTop);
  const ticks = axisTicks(min, max, scaleMode);
  const sampleGroups = new Map<string, EvidencePoint[]>();
  panel.points.forEach((point) => {
    const group = point.location || point.label;
    sampleGroups.set(group, [...(sampleGroups.get(group) || []), point]);
  });
  const sampleTimes = panel.points
    .map((point) => Date.parse(point.measuredAt || ""))
    .filter(Number.isFinite);
  const sampleMinTime = sampleTimes.length > 0 ? Math.min(...sampleTimes) : 0;
  const sampleMaxTime = sampleTimes.length > 0 ? Math.max(...sampleTimes) : 0;
  const xForSampleTime = (time: number) => sampleMaxTime === sampleMinTime
    ? (plotLeft + plotRight) / 2
    : plotLeft + ((time - sampleMinTime) / (sampleMaxTime - sampleMinTime)) * (plotRight - plotLeft);
  const sampleTickTimes = sampleTimes.length > 0
    ? Array.from({ length: Math.min(4, sampleTimes.length) }, (_, index) => {
      const fraction = Math.min(1, index / Math.max(Math.min(4, sampleTimes.length) - 1, 1));
      return sampleMinTime + (sampleMaxTime - sampleMinTime) * fraction;
    })
    : [];
  const groupColors = ["#087f8c", "#f59e0b", "#7c3aed", "#dc2626", "#2563eb", "#059669"];
  const tableLabel = `${panel.title}${panel.unit ? `, ${panel.unit}` : ""}`;
  const selectedPoint = panel.points.find((point) => point.id === selectedPointId);
  const pointDescription = (point: EvidencePoint) => {
    const date = panel.mode === "ranking" || panel.mode === "aggregates"
      ? evidenceWindow(point)
      : point.measuredAt ? formatDate(point.measuredAt) : evidenceWindow(point);
    return `${point.label}: ${point.value === undefined ? "sin valor" : `${formatChartValue(point.value)} ${panel.unit || ""}`} · ${date}`;
  };
  const handlePointKeyDown = (event: KeyboardEvent<SVGGElement>, point: EvidencePoint) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setSelectedPointId(point.id);
    }
  };

  return (
    <Box className={classes.evidencePanel}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1} alignItems={{ sm: "center" }}>
        <Box>
          <Typography variant="subtitle2" className={classes.findingChartTitle}>{panel.title}</Typography>
          {panel.explanation && <Typography variant="caption" className={classes.evidenceExplanation}>{panel.explanation}</Typography>}
        </Box>
        {canLogarithmic && (
          <Button
            size="small"
            aria-label={`Cambiar escala, actualmente ${scaleMode === "linear" ? "lineal" : "logarítmica"}`}
            onClick={() => setScaleMode((current) => current === "linear" ? "logarithmic" : "linear")}
          >
            Escala: {scaleMode === "linear" ? "lineal" : "logarítmica"} · cambiar
          </Button>
        )}
      </Stack>
      {panel.mode !== "quality" && (
        <svg className={classes.findingChartSvg} viewBox={`0 0 600 ${chartHeight}`} role="img" aria-label={tableLabel}>
          {isHorizontal ? (
            <>
              {ticks.map((tick) => {
                const x = xForValue(tick);
                return (
                  <g key={tick}>
                    <line x1={x} x2={x} y1={plotTop - 8} y2={plotBottom} className={classes.findingGridLine} />
                    <text x={x} y={plotTop - 12} textAnchor="middle" className={classes.findingAxisLabel}>{formatChartValue(tick)}</text>
                  </g>
                );
              })}
              {panel.points.map((point, index) => {
                const y = plotTop + index * 32 + 10;
                const x = xForValue(point.value || 0);
                return (
                  <g
                    key={point.id}
                    tabIndex={0}
                    role="button"
                    aria-label={pointDescription(point)}
                    onClick={() => setSelectedPointId(point.id)}
                    onKeyDown={(event) => handlePointKeyDown(event, point)}
                    className={classes.findingInteractive}
                  >
                    <text x={plotLeft - 8} y={y + 4} textAnchor="end" className={classes.findingAxisLabel}>{compactText(point.label, 22)}</text>
                    <line x1={plotLeft} x2={x} y1={y} y2={y} className={classes.findingBarLine} />
                    <circle cx={x} cy={y} r={6} className={classes.findingPointCurrent}>
                      <title>{pointDescription(point)}</title>
                    </circle>
                  </g>
                );
              })}
            </>
          ) : (
            <>
              {ticks.map((tick) => {
                const y = yFor(tick);
                return (
                  <g key={tick}>
                    <line x1={plotLeft} x2={plotRight} y1={y} y2={y} className={classes.findingGridLine} />
                    <text x={plotLeft - 7} y={y + 4} textAnchor="end" className={classes.findingAxisLabel}>{formatChartValue(tick)}</text>
                  </g>
                );
              })}
              {panel.mode === "comparison" && panel.points.map((point, index) => {
                const x = xFor(index);
                const referenceY = point.reference === undefined ? null : yFor(point.reference);
                const currentY = point.current === undefined ? null : yFor(point.current);
                return (
                  <g
                    key={point.id}
                    tabIndex={0}
                    role="button"
                    aria-label={`${point.label}: referencia ${point.reference === undefined ? "sin dato" : formatChartValue(point.reference)} ${panel.unit || ""}; reciente ${point.current === undefined ? "sin dato" : formatChartValue(point.current)} ${panel.unit || ""}`}
                    onClick={() => setSelectedPointId(point.id)}
                    onKeyDown={(event) => handlePointKeyDown(event, point)}
                    className={classes.findingInteractive}
                  >
                    {referenceY !== null && currentY !== null && <line x1={x} x2={x} y1={referenceY} y2={currentY} className={classes.findingComparisonLine} />}
                    {referenceY !== null && <circle cx={x} cy={referenceY} r={4} className={classes.findingPointReference}><title>{`${point.label}: referencia ${formatChartValue(point.reference || 0)} ${panel.unit || ""}`}</title></circle>}
                    {currentY !== null && <circle cx={x} cy={currentY} r={5} className={classes.findingPointCurrent}><title>{`${point.label}: reciente ${formatChartValue(point.current || 0)} ${panel.unit || ""}`}</title></circle>}
                    <text x={x} y={188} textAnchor="middle" className={classes.findingAxisLabel}>{compactText(point.label, 16)}</text>
                  </g>
                );
              })}
              {panel.mode === "samples" && (
                <>
                  <line x1={plotLeft} x2={plotRight} y1={plotBottom} y2={plotBottom} className={classes.findingGridLine} />
                  {sampleTickTimes.map((time) => (
                    <text key={time} x={xForSampleTime(time)} y={202} textAnchor="middle" className={classes.findingAxisLabel}>
                      {evolutionDateLabel(new Date(time).toISOString(), "—")}
                    </text>
                  ))}
                </>
              )}
              {panel.mode === "samples" && Array.from(sampleGroups.entries()).map(([location, points], groupIndex) => {
                const ordered = [...points].sort((a, b) => Date.parse(a.measuredAt || "") - Date.parse(b.measuredAt || ""));
                const pointsString = ordered
                  .filter((point) => point.measuredAt && Number.isFinite(Date.parse(point.measuredAt)))
                  .map((point) => `${xForSampleTime(Date.parse(point.measuredAt || ""))},${yFor(point.value || 0)}`)
                  .join(" ");
                return (
                  <g key={location}>
                    {ordered.length > 1 && <polyline points={pointsString} className={classes.findingLine} style={{ stroke: groupColors[groupIndex % groupColors.length] }} />}
                    {ordered.map((point) => (
                      <circle
                        key={point.id}
                        cx={xForSampleTime(Date.parse(point.measuredAt || ""))}
                        cy={yFor(point.value || 0)}
                        r={5}
                        tabIndex={0}
                        role="button"
                        aria-label={pointDescription(point)}
                        className={`${classes.findingPointCurrent} ${classes.findingInteractive}`}
                        style={{ fill: groupColors[groupIndex % groupColors.length] }}
                        onClick={() => setSelectedPointId(point.id)}
                        onKeyDown={(event) => handlePointKeyDown(event as KeyboardEvent<SVGGElement>, point)}
                      >
                        <title>{`${point.label}: ${formatChartValue(point.value || 0)} ${panel.unit || ""} · ${formatDate(point.measuredAt || "")}`}</title>
                      </circle>
                    ))}
                  </g>
                );
              })}
            </>
          )}
        </svg>
      )}
      {panel.mode === "comparison" && (
        <Box className={classes.findingLegend} aria-label="Leyenda del gráfico">
          <span><i className={classes.findingLegendReference} /> Referencia</span>
          <span><i className={classes.findingLegendCurrent} /> Reciente</span>
        </Box>
      )}
      {panel.mode === "samples" && (
        <Box className={classes.findingLegend} aria-label="Leyenda del gráfico">
          {Array.from(sampleGroups.keys()).map((location, index) => (
            <span key={location}><i style={{ background: groupColors[index % groupColors.length] }} /> {formatLocation(location)}</span>
          ))}
        </Box>
      )}
      {selectedPoint && (
        <Box className={classes.selectedPoint} role="status">
          {pointDescription(selectedPoint)}
        </Box>
      )}
      <Typography variant="caption" className={classes.evidenceCount}>
        {panel.totalCount !== undefined && panel.totalCount > panel.points.length
          ? `Mostrando ${panel.points.length} de ${panel.totalCount} observaciones`
          : `${panel.points.length} ${panel.mode === "ranking" ? "estaciones" : panel.mode === "aggregates" ? "agregados" : "puntos visibles"}`}
        {scaleMode === "logarithmic" && " · escala logarítmica"}
      </Typography>
      {sourceRef && panel.totalCount !== undefined && panel.totalCount > panel.points.length && (
        <Button component="a" href={sourceRef} target="_blank" rel="noreferrer" size="small" sx={{ alignSelf: "flex-start" }}>
          Ver {panel.totalCount} registros en la fuente
        </Button>
      )}
      <Box className={classes.evidenceTableWrap}>
        <table className={classes.evidenceTable} aria-label={tableLabel}>
          <thead>
            <tr>
              <th scope="col">Estación / serie</th>
              {panel.mode === "comparison" ? <><th scope="col">Referencia</th><th scope="col">Reciente</th></> : <th scope="col">Valor</th>}
              <th scope="col">Fecha / ventana</th>
            </tr>
          </thead>
          <tbody>
            {panel.points.map((point) => (
              <tr key={point.id}>
                <th scope="row">{point.label}</th>
                {panel.mode === "comparison" ? (
                  <>
                    <td data-label="Referencia">{point.reference === undefined ? "—" : `${formatChartValue(point.reference)} ${panel.unit || ""}`}</td>
                    <td data-label="Reciente">{point.current === undefined ? "—" : `${formatChartValue(point.current)} ${panel.unit || ""}`}</td>
                  </>
                ) : (
                  <td data-label="Valor">{point.value === undefined ? "—" : `${formatChartValue(point.value)} ${panel.unit || ""}`}</td>
                )}
                <td data-label="Fecha / ventana">
                  {panel.mode === "aggregates" || panel.mode === "ranking" ? evidenceWindow(point) : point.measuredAt ? formatDate(point.measuredAt) : evidenceWindow(point)}
                  {point.sampleCount !== undefined && ` · n=${point.sampleCount}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

function qualityStatusLabel(status: string | undefined) {
  switch (status) {
    case "available":
    case "current":
      return "Fuente accesible";
    case "empty":
      return "Serie sin datos";
    case "stale":
      return "Dato antiguo";
    case "flagged":
      return "Valor a revisar";
    default:
      return status || "Estado no determinado";
  }
}

function ageAtPublication(hours: number | undefined) {
  return hours === undefined ? "—" : `${Math.round(hours / 24)} días al publicar`;
}

function FindingQualityPanel({ panel }: { panel: EvidencePanel }) {
  return (
    <Box className={classes.evidencePanel}>
      <Typography variant="subtitle2" className={classes.findingChartTitle}>{panel.title}</Typography>
      {panel.explanation && <Typography variant="caption" className={classes.evidenceExplanation}>{panel.explanation}</Typography>}
      {panel.asOf && (
        <Typography variant="caption" className={classes.evidenceCount}>
          Antigüedad calculada al publicar: {formatDate(panel.asOf)}
        </Typography>
      )}
      <Box className={classes.evidenceTableWrap}>
        <table className={classes.evidenceTable} aria-label={panel.title}>
          <thead>
            <tr>
              <th scope="col">Fuente / estación</th>
              <th scope="col">Estado</th>
              <th scope="col">Medición / antigüedad</th>
              <th scope="col">Recepción / antigüedad</th>
              <th scope="col">Origen</th>
            </tr>
          </thead>
          <tbody>
            {panel.points.map((point) => (
              <tr key={point.id}>
                <th scope="row">{point.label}</th>
                <td data-label="Estado">{qualityStatusLabel(point.qualityStatus)}</td>
                <td data-label="Medición">
                  {point.measuredAt ? formatDate(point.measuredAt) : "—"}
                  {point.sampleAgeHours !== undefined && ` · ${ageAtPublication(point.sampleAgeHours)}`}
                </td>
                <td data-label="Recepción">
                  {point.receivedAt ? formatDate(point.receivedAt) : "—"}
                  {point.receivedAgeHours !== undefined && ` · ${ageAtPublication(point.receivedAgeHours)}`}
                </td>
                <td data-label="Origen">{point.sourceTable || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

function FindingDetail({ candidate }: { candidate: FeedCandidate }) {
  const panels = buildEvidencePanels(candidate);
  const facts = candidate.facts || {};
  const sourceRef = candidate.source_refs[0];
  const metricItems = [
    { label: "Indicadores", value: String(panels.length) },
    { label: "Puntos visibles", value: String(panels.reduce((total, panel) => total + panel.points.length, 0)) },
    { label: "Corte", value: typeof facts.snapshot_id === "string" ? facts.snapshot_id : "No disponible" },
  ];

  return (
    <Box className={classes.findingDetail}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
        <Typography variant="subtitle2" fontWeight={800}>Evidencia del hallazgo</Typography>
        <Typography variant="caption" color="text.secondary">{confidenceLabels[candidate.confidence]}</Typography>
      </Stack>
      <Typography variant="body2" className={classes.findingDetailSummary}>{candidate.summary}</Typography>
      <Box className={classes.findingMetricGrid}>
        {metricItems.map((metric) => (
          <Box key={metric.label} className={classes.findingMetric}>
            <Typography variant="caption" className={classes.findingMetricLabel}>{metric.label}</Typography>
            <Typography variant="body2" className={classes.findingMetricValue}>{metric.value}</Typography>
          </Box>
        ))}
      </Box>
      {panels.length === 0 ? (
        <Box className={classes.findingChartEmpty}>
          Esta publicación no incluye evidencia numérica estructurada para graficar. Se conserva el texto y el acceso a las fuentes.
        </Box>
      ) : (
        <Stack gap={1.5}>
          {panels.map((panel) => panel.mode === "quality"
            ? <FindingQualityPanel key={panel.key} panel={panel} />
            : <FindingEvidencePanel key={panel.key} panel={panel} sourceRef={sourceRef} />)}
        </Stack>
      )}
    </Box>
  );
}
function cleanPublicationText(value: string) {
  return value
    .replace(/del patrón del patrón/gi, "del patrón")
    .replace(/tres señales en tres puntos/gi, "tres señales; consulte los puntos en la evidencia")
    .replace(/\s+/g, " ")
    .trim();
}

function reflectPreview(candidate: FeedCandidate) {
  const trigger = typeof candidate.facts?.trigger === "string" ? candidate.facts.trigger : "";
  const guidance = cleanPublicationText(candidate.summary.trim());
  const finding = cleanPublicationText(trigger || candidate.title);
  return {
    title: "Hallazgo reflexivo",
    summary: `Qué encontró: ${compactText(finding, 220)}. ${compactText(guidance, 180)}`,
  };
}
function anomalyPreview(candidate: FeedCandidate) {
  const rows = anomalyRows(candidate);
  const microbiology = microbiologyRows(candidate);
  if (!rows.length && microbiology.length) {
    return {
      title: "Desvíos históricos de microbiología",
      summary: `${microbiology.length} playas con valores de cf fuera de referencia. Los valores máximos y las medias geométricas se muestran abajo.`,
    };
  }
  if (!rows.length) return null;
  const attributes = Array.from(new Set(rows.map((row) => String(row.attribute || ""))));
  const directions = Array.from(new Set(rows.map((row) => String(row.direction || ""))));
  const confidence = rows.some((row) => Number(row.recent_samples || 0) < 3) ? "baja" : "media";
  const attribute = attributes.length === 1
    ? anomalyAttributeLabels[attributes[0]] || attributes[0]
    : "condiciones costeras";
  const direction = directions.length === 1
    ? directions[0] === "above" ? "por encima" : "por debajo"
    : "fuera del patrón";
  return {
    title: `${attribute} ${direction} del patrón histórico`,
    summary: `${rows.length} señales en ${rows.length === 1 ? "un punto" : `${rows.length} puntos`}. Comparación de 7 días recientes contra 30 días de referencia. Confianza ${confidence}.`,
  };
}
function observationPreview(candidate: FeedCandidate) {
  const panels = buildMicrobiologyPanels(candidate);
  if (panels.length === 0) return null;
  const counts = panels.map((panel) => {
    const total = panel.totalCount ?? panel.points.length;
    const noun = panel.mode === "aggregates" ? "agregados MG5" : "muestras";
    return `${total} ${noun} de ${panel.title.toLowerCase()}`;
  });
  const visible = panels.reduce((total, panel) => total + panel.points.length, 0);
  const available = panels.reduce((total, panel) => total + (panel.totalCount ?? panel.points.length), 0);
  return {
    title: candidate.title,
    summary: `${counts.join(", ")}. Se muestran ${visible} registros trazables${available > visible ? ` de ${available}` : ""}; las ventanas y fechas se consultan en la evidencia.`,
  };
}

function factChips(candidate: FeedCandidate) {
  const facts = candidate.facts || {};
  const chips: string[] = [];

  if (typeof facts.beaches_with_data === "number") {
    chips.push(`${facts.beaches_with_data}/${facts.beaches_expected} playas con datos`);
  }
  if (typeof facts.window_days === "number") {
    chips.push(`ventana histórica ${facts.window_days} días`);
  }
  if (typeof facts.outside_count === "number") {
    chips.push(`${facts.outside_count} fuera del rango previo`);
  }
  if (typeof facts.temperature_missing_count === "number") {
    chips.push(`${facts.temperature_missing_count} sin temperatura`);
  }
  if (typeof facts.max_sample_age_hours === "number") {
    chips.push(`muestra más antigua: ${Math.round(facts.max_sample_age_hours / 24)} días`);
  }

  return chips;
}

function dataInfoDescription(candidate: FeedCandidate) {
  switch (candidate.kind) {
    case "data_quality":
      return "Resume si las fuentes microbiológicas están disponibles, qué tan recientes son y cuántos parámetros tienen datos. No equivale a una declaración de aptitud para baño.";
    case "beach_ranking":
      return "Ordena las playas con las métricas microbiológicas disponibles en la ventana indicada. El ranking ayuda a comparar datos y no reemplaza los comunicados oficiales.";
    case "observation":
      return "Muestra mediciones directas de laboratorio y sus fechas. Son observaciones puntuales: no sustituyen la evaluación sanitaria basada en varias muestras.";
    case "anomaly":
      return "Compara el período reciente contra una referencia histórica para señalar cambios fuera del patrón habitual.";
    case "reflect_bead":
      return "Es un resumen contextual generado a partir de señales ambientales y sus fuentes. La información técnica de respaldo permanece disponible en el detalle.";
    default:
      return "Esta señal reúne datos ambientales, su contexto y las fuentes disponibles para este corte.";
  }
}

function candidateDataAvailableThrough(candidate: FeedCandidate) {
  const facts = candidate.facts || {};
  const microbiology = asRecord(facts.microbiology);
  return stringValue(facts.data_available_through)
    || stringValue(facts.latest_sample_at)
    || stringValue(microbiology?.data_available_through)
    || stringValue(microbiology?.latest_sample_at);
}

function dataInfoFacts(candidate: FeedCandidate) {
  const facts = candidate.facts || {};
  const values: Array<{ label: string; value: string }> = [
    { label: "Tipo", value: kindLabels[candidate.kind] || candidate.kind },
    { label: "Confianza", value: confidenceLabels[candidate.confidence] },
  ];
  const snapshot = typeof facts.snapshot_id === "string" ? facts.snapshot_id : "";
  const windowDays = finiteNumber(facts.quality_window_days ?? facts.window_days);
  const latestSample = typeof facts.latest_sample_at === "string"
    ? facts.latest_sample_at
    : candidateDataAvailableThrough(candidate);
  if (snapshot) values.push({ label: "Corte de datos", value: snapshot });
  if (windowDays !== null) values.push({ label: "Ventana analizada", value: `${windowDays} días` });
  if (latestSample) values.push({ label: "Datos hasta", value: formatDate(latestSample) });
  return values;
}
type EnvironmentalFeedShareData = ShareData & { url: string };
type EnvironmentalFeedShareMethod = (data: ShareData) => Promise<void>;
type EnvironmentalFeedShareResult = "shared" | "copied" | "cancelled";

export async function performEnvironmentalFeedShare(
  data: EnvironmentalFeedShareData,
  share: EnvironmentalFeedShareMethod | undefined,
  copy: (url: string) => Promise<void>,
): Promise<EnvironmentalFeedShareResult> {
  if (share) {
    try {
      await share(data);
      return "shared";
    } catch (caught) {
      const errorName = caught instanceof DOMException
        ? caught.name
        : (typeof caught === "object" && caught !== null && "name" in caught
          ? String(caught.name)
          : "");
      if (errorName === "AbortError") return "cancelled";
    }
  }

  await copy(data.url);
  return "copied";
}

async function copyEnvironmentalFeedShareUrl(shareUrl: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(shareUrl);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = shareUrl;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();
  try {
    if (!document.execCommand("copy")) {
      throw new Error("El navegador no permite copiar el enlace.");
    }
  } finally {
    textArea.remove();
  }
}

function FeedCard({
  impressionId,
  item,
  submittedAction,
  onFeedback,
  onContextSelect,
  history,
  mobile,
}: {
  impressionId: string;
  item: FeedItem;
  submittedAction?: FeedbackAction;
  onFeedback: (candidateId: string, action: FeedbackAction) => Promise<void>;
  onContextSelect: (contextId: string) => void;
  history: FeedResponse[];
  mobile: boolean;
}) {
  const candidate = item.candidate;
  const isReflect = candidate.kind === "reflect_bead";
  const [showDetails, setShowDetails] = useState(false);
  const [shareState, setShareState] = useState<"idle" | "copied">("idle");
  const [sharePending, setSharePending] = useState(false);
  const shareInProgressRef = useRef(false);
  const shareResetTimerRef = useRef<number | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [dialog, setDialog] = useState<"info" | "history" | null>(null);
  const [saved, setSaved] = useState(() => loadSavedEnvironmentalSignalIds().includes(candidate.candidate_id));
  const historyEntries = history
    .flatMap((feed) => feed.response.items
      .filter((historyItem) => historyItem.candidate.candidate_id === candidate.candidate_id)
      .map((historyItem) => ({ feed, item: historyItem })))
    .sort((a, b) => Date.parse(b.feed.created_at) - Date.parse(a.feed.created_at));
  const kind = kindLabels[candidate.kind] || candidate.kind;
  const chips = factChips(candidate);
  const icon = kindIcons[candidate.kind] || <FactCheckOutlinedIcon fontSize="small" />;
  const anomalyValues = candidate.kind === "anomaly" ? anomalyRows(candidate) : [];
  const microbiologyValues = candidate.kind === "anomaly" ? microbiologyRows(candidate) : [];
  const behaviorValues = candidate.kind === "observation" ? historicalObservationRows(candidate) : [];
  const currentColiformValues = candidate.kind === "observation" ? currentColiformRows(candidate) : [];
  const rankingValues = candidate.kind === "beach_ranking" ? beachRankingRows(candidate) : [];
  const anomaly = candidate.kind === "anomaly" ? anomalyPreview(candidate) : null;
  const observation = candidate.kind === "observation" ? observationPreview(candidate) : null;
  const reflect = isReflect ? reflectPreview(candidate) : null;
  const sourceRef = candidate.source_refs[0];
  const title = isReflect && !showDetails
    ? reflect?.title
    : anomaly?.title || observation?.title || candidate.title;
  const summary = isReflect && !showDetails
    ? reflect?.summary
    : anomaly?.summary || observation?.summary || cleanPublicationText(candidate.summary);
  const handleToggleSaved = () => {
    const ids = loadSavedEnvironmentalSignalIds();
    const nextSaved = !saved;
    const nextIds = nextSaved
      ? Array.from(new Set([...ids, candidate.candidate_id]))
      : ids.filter((id) => id !== candidate.candidate_id);
    persistSavedEnvironmentalSignalIds(nextIds);
    setSaved(nextSaved);
    setMenuAnchor(null);
  };

  const openDialog = (nextDialog: "info" | "history") => {
    setDialog(nextDialog);
    setMenuAnchor(null);
  };

  const handleShare = async () => {
    if (shareInProgressRef.current) return;

    shareInProgressRef.current = true;
    setSharePending(true);
    const shareUrl = getEnvironmentalPublicationShareUrl(candidate.candidate_id);
    try {
      const result = await performEnvironmentalFeedShare(
        {
          title: candidate.title,
          text: compactText(candidate.summary, 180),
          url: shareUrl,
        },
        typeof navigator.share === "function" ? navigator.share.bind(navigator) : undefined,
        copyEnvironmentalFeedShareUrl,
      );
      if (result === "copied") {
        setShareState("copied");
        if (shareResetTimerRef.current !== null) {
          window.clearTimeout(shareResetTimerRef.current);
        }
        shareResetTimerRef.current = window.setTimeout(() => setShareState("idle"), 2200);
      }
    } catch {
      setShareState("idle");
    } finally {
      shareInProgressRef.current = false;
      setSharePending(false);
    }
  };

  return (
    <Paper className={mobile ? classes.mobileFeedCard : classes.feedCard} elevation={0} component="article">
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Stack direction="row" gap={1.5} alignItems="center">
          <Avatar className={classes.avatar}>
            <WavesIcon />
          </Avatar>
          <Box>
            <Typography className={classes.author}>Protea · Observaciones ambientales</Typography>
            <Typography variant="caption" color="text.secondary">
              Publicado {formatDate(candidate.published_at)}{candidateDataAvailableThrough(candidate) ? ` · Datos hasta ${formatDate(candidateDataAvailableThrough(candidate) || "")}` : ""} · {confidenceLabels[candidate.confidence]}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" gap={0.25}>
          <Tooltip title={sharePending ? "Compartiendo…" : shareState === "copied" ? "Enlace copiado" : "Compartir publicación"}>
            <IconButton
              aria-label="Compartir publicación"
              aria-busy={sharePending}
              disabled={sharePending}
              size="small"
              onClick={() => void handleShare()}
            >
              <ShareOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label="Más opciones"
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            size="small"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
          >
            <MoreHorizIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        MenuListProps={{ "aria-label": "Acciones de la señal ambiental" }}
      >
        <MenuItem onClick={() => openDialog("info")}>
          <ListItemIcon><InfoOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Información de los datos" />
        </MenuItem>
        <MenuItem onClick={handleToggleSaved}>
          <ListItemIcon>
            {saved ? <BookmarkRoundedIcon fontSize="small" /> : <BookmarkBorderRoundedIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText primary={saved ? "Quitar de guardadas" : "Guardar señal"} />
        </MenuItem>
        <MenuItem onClick={() => openDialog("history")}>
          <ListItemIcon><HistoryOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Historial de cambios" secondary={`${historyEntries.length} versiones disponibles`} />
        </MenuItem>
      </Menu>

      <Dialog open={dialog === "info"} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Información de los datos</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>{dataInfoDescription(candidate)}</Typography>
          <Stack gap={1}>
            {dataInfoFacts(candidate).map((fact) => (
              <Stack key={fact.label} direction="row" justifyContent="space-between" gap={2}>
                <Typography variant="body2" color="text.secondary">{fact.label}</Typography>
                <Typography variant="body2" textAlign="right" fontWeight={700}>{fact.value}</Typography>
              </Stack>
            ))}
          </Stack>
          {candidate.source_refs.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2, overflowWrap: "anywhere" }}>
              Fuentes: {candidate.source_refs.join(" · ")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === "history"} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Historial de cambios</DialogTitle>
        <DialogContent dividers>
          {historyEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Todavía no hay otras versiones de esta señal para comparar.
            </Typography>
          ) : (
            <Stack gap={1.5}>
              {historyEntries.map(({ feed, item: historyItem }, index) => (
                <Box key={`${feed.impression_id}-${index}`} className={classes.historyItem}>
                  <Typography variant="subtitle2">
                    {formatDate(historyItem.candidate.published_at)}
                  </Typography>
                  <Typography variant="body2">{compactText(historyItem.candidate.summary, 320)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Generado {formatDate(feed.created_at)} · {confidenceLabels[historyItem.candidate.confidence]}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>


      <Stack direction="row" gap={0.75} alignItems="center" className={classes.kindLabel}>
        {icon}
        <Typography variant="caption" fontWeight={700}>
          {kind}
        </Typography>
      </Stack>
      <Typography variant="h5" className={classes.cardTitle}>{title}</Typography>
      <Typography className={classes.summary}>{summary}</Typography>
      <Button
        size="small"
        onClick={() => setShowDetails((current) => !current)}
        aria-expanded={showDetails}
        className={classes.detailToggleButton}
      >
        {showDetails ? "Ocultar evidencia" : "Explorar evidencia"}
      </Button>
      {showDetails && (
        <>
      <FindingDetail candidate={candidate} />
      {currentColiformValues.length > 0 && (
        <Box className={classes.anomalyList}>
          <Typography variant="subtitle2">Últimas muestras directas de CF</Typography>
          {currentColiformValues.map((row, index) => (
            <Typography key={`${String(row.beach)}-${String(row.timestamp)}-${index}`} variant="body2" className={classes.anomalyRow}>
              <strong>{formatLocation(String(row.beach || "Playa"))}</strong>: {formatAnomalyValue(row.value_cf_100ml)} NMP/100 ml · {String(row.timestamp || "—")}
            </Typography>
          ))}
        </Box>
      )}
      {behaviorValues.length > 0 && (
        <Box className={classes.anomalyList}>
          <Typography variant="subtitle2">Comportamiento reciente</Typography>
          {behaviorValues.map((row, index) => {
            const attribute = String(row.attribute || "");
            const label = row.role === "mareografica"
              ? "Marea ANP"
              : anomalyAttributeLabels[attribute] || attribute;
            const delta = typeof row.delta === "number" && row.delta >= 0
              ? `+${formatAnomalyValue(row.delta)}`
              : formatAnomalyValue(row.delta);
            return (
              <Typography key={`${String(row.source)}-${attribute}-${index}`} variant="body2" className={classes.anomalyRow}>
                <strong>{formatLocation(String(row.source || "Punto"))}</strong>: {label} {formatAnomalyValue(row.recent_median)} {String(row.unit || "")} · base {formatAnomalyValue(row.baseline_median)} · Δ {delta}
              </Typography>
            );
          })}
        </Box>
      )}
      {anomalyValues.length > 0 && (
        <Box className={classes.anomalyList}>
          <Typography variant="subtitle2">Valores desviados</Typography>
          {anomalyValues.map((row, index) => {
            const attribute = String(row.attribute || "");
            const delta = typeof row.delta === "number" && row.delta >= 0
              ? `+${formatAnomalyValue(row.delta)}`
              : formatAnomalyValue(row.delta);
            return (
              <Typography key={`${String(row.source)}-${attribute}-${index}`} variant="body2" className={classes.anomalyRow}>
                <strong>{formatLocation(String(row.source || "Punto"))}</strong>: {anomalyAttributeLabels[attribute] || attribute} {formatAnomalyValue(row.recent_median)} {String(row.unit || "")} · base {formatAnomalyValue(row.baseline_median)} · Δ {delta}
              </Typography>
            );
          })}
        </Box>
      )}
      {microbiologyValues.length > 0 && (
        <Box className={classes.anomalyList}>
          <Typography variant="subtitle2">Valores microbiológicos desviados</Typography>
          {microbiologyValues.map((row) => (
            <Typography key={row.beach} variant="body2" className={classes.anomalyRow}>
              <strong>{formatLocation(row.beach)}</strong>: {row.details}
            </Typography>
          ))}
        </Box>
      )}
      {rankingValues.length > 0 && (
        <Box className={classes.anomalyList}>
          <Typography variant="subtitle2">
            Ranking por {candidate.facts?.ranking_parameter === "enterococos_mg5" ? "enterococos mg5" : "datos de CF"}
          </Typography>
          {rankingValues.map((row, index) => {
            const alerts = Array.isArray(row.regulatory_alerts)
              ? row.regulatory_alerts.map(String)
              : [];
            const aggregateSamples = typeof row.aggregate_sample_count === "number"
              ? ` · agregado de ${row.aggregate_sample_count} muestras`
              : "";
            const metricDetails = typeof row.value_mg5_100ml === "number"
              ? `mg5 ${formatAnomalyValue(row.value_mg5_100ml)} NMP/100 ml${aggregateSamples}`
              : `máx. cf ${formatAnomalyValue(row.max_cf_100ml)} · media geométrica ${formatAnomalyValue(row.geomean_cf_100ml)} · ${String(row.sample_count || 0)} muestras`;
            return (
              <Box key={`${String(row.beach)}-${index}`}>
                <Typography variant="body2" className={classes.anomalyRow}>
                  <strong>{String(row.rank || index + 1)}. {formatLocation(String(row.beach || "Playa"))}</strong>: {metricDetails} · ventana {String(row.window_start || "—")} a {String(row.window_end || "—")}
                </Typography>
                {alerts.length > 0 && (
                  <Typography variant="caption" color="warning.main" className={classes.anomalyRow}>
                    Alerta regulatoria: {alerts.join(" · ")}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
        </>
      )}
      {isReflect && showDetails && (
        <Box component="details" className={classes.detailPanel}>
          <Typography component="summary" variant="subtitle2" sx={{ cursor: "pointer" }}>
            Metadatos técnicos de la reflexión
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>{candidate.title}</Typography>
          {Object.keys(candidate.facts || {}).length > 0 && (
            <Typography component="pre" variant="caption" className={classes.detailFacts}>
              {JSON.stringify(candidate.facts, null, 2)}
            </Typography>
          )}
        </Box>
      )}

      {chips.length > 0 && (
        <Stack direction="row" gap={0.75} flexWrap="wrap" className={classes.facts}>
          {chips.map((chip) => <Chip key={chip} label={chip} size="small" variant="outlined" />)}
        </Stack>
      )}

      {candidate.contexts.length > 0 && (
        <Stack direction="row" gap={0.75} flexWrap="wrap" className={classes.contexts}>
          {candidate.contexts.slice(0, mobile ? 3 : 5).map((context) => (
            <Chip
              key={context.context_id}
              label={context.label}
              title={`Abrir ${context.label} en el mapa`}
              aria-label={`Abrir ${context.label} en el mapa`}
              size="small"
              onClick={() => onContextSelect(context.context_id)}
            />
          ))}
          {candidate.contexts.length > 5 && (
            <Chip label={`+${candidate.contexts.length - 5}`} size="small" />
          )}
        </Stack>
      )}

      <Divider className={classes.divider} />
      {showDetails && (
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
        <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
          <Tooltip title="Me gusta este tipo de publicación">
            <span>
              <IconButton
                className={classes.heartButton}
                aria-label="Me gusta este tipo de publicación"
                aria-pressed={submittedAction === "useful"}
                size="small"
                color={submittedAction === "useful" ? "error" : "default"}
                style={submittedAction === "useful" ? { color: "#f43f5e", opacity: 1 } : undefined}
                onClick={() => onFeedback(candidate.candidate_id, "useful")}
                disabled={Boolean(submittedAction)}
              >
                {submittedAction === "useful" ? (
                  <FavoriteIcon fontSize="small" />
                ) : (
                  <FavoriteBorderOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" className={classes.preferenceHint}>
            Me gusta · priorizar este tipo
          </Typography>
          <Tooltip title="No es útil">
            <span>
              <IconButton
                aria-label="No es útil"
                size="small"
                color={submittedAction === "not_useful" ? "error" : "default"}
                onClick={() => onFeedback(candidate.candidate_id, "not_useful")}
                disabled={Boolean(submittedAction)}
              >
                <ThumbDownAltOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="small"
            onClick={() => onFeedback(candidate.candidate_id, "needs_more_context")}
            disabled={Boolean(submittedAction)}
          >
            Necesito más contexto
          </Button>
        </Stack>
        {sourceRef && (
          <Link
            href={sourceRef}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            className={classes.sourceLink}
            aria-label={isReflect ? "Ver datos fuente de la reflexión" : "Abrir fuente"}
          >
            {isReflect ? "Ver datos fuente" : "Abrir fuente"} <OpenInNewIcon fontSize="inherit" />
          </Link>
        )}
      </Stack>
      )}
      {submittedAction && (
        <Typography variant="caption" color="primary" className={classes.feedbackState}>
          Feedback registrado: {submittedAction === "useful" ? "me gusta" : submittedAction.replaceAll("_", " ")}
        </Typography>
      )}
    </Paper>
  );
}

export default function EnvironmentalFeedPage() {
  const { setTitle } = useContext(TitleContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useResponsiveLayout() === "mobile";
  const [feeds, setFeeds] = useState<FeedResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [feedback, setFeedback] = useState<Record<string, FeedbackAction>>({});
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const publicationId = searchParams.get("publication");
  const savedOnly = searchParams.get("saved") === "1";
  const [sharedPublication, setSharedPublication] = useState<PublicEnvironmentalPublication | null>(null);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [sharedError, setSharedError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState(false);
  const fullscreenErrorTimerRef = useRef<number | null>(null);
  useEffect(() => {
    trackAnalyticsEvent("environmental_feed_view", { feed_id: "environmental_feed" });
  }, []);

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === document.documentElement);
    };
    document.addEventListener("fullscreenchange", syncFullscreenState);
    syncFullscreenState();
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreenState);
      if (fullscreenErrorTimerRef.current !== null) {
        window.clearTimeout(fullscreenErrorTimerRef.current);
      }
    };
  }, []);

  const showFullscreenError = () => {
    setFullscreenError(true);
    if (fullscreenErrorTimerRef.current !== null) {
      window.clearTimeout(fullscreenErrorTimerRef.current);
    }
    fullscreenErrorTimerRef.current = window.setTimeout(() => {
      setFullscreenError(false);
    }, 3500);
  };

  const toggleFullscreen = async () => {
    setFullscreenError(false);
    if (!document.fullscreenEnabled || !document.documentElement.requestFullscreen) {
      showFullscreenError();
      return;
    }
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen({ navigationUI: "hide" });
      }
    } catch {
      showFullscreenError();
    }
  };

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const loaded: FeedResponse[] = [];
      const latest = await getLatestEnvironmentalFeed().catch(() => null);
      if (latest) {
        loaded.push(latest);
      }

      const seenCursors = new Set<string>();
      let before: string | undefined;
      let hasMore = true;

      while (hasMore) {
        const result = await getEnvironmentalFeedHistory(before, 50);
        loaded.push(...result.items);

        const cursor = result.next_before;
        if (!result.has_more || !cursor || seenCursors.has(cursor)) {
          break;
        }
        seenCursors.add(cursor);
        before = cursor;
      }

      const unique = new Map(loaded.map((item) => [item.impression_id, item]));
      const ordered = Array.from(unique.values()).sort(
        (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)
      );
      setFeeds(ordered);
      setError(null);
    } catch {
      setError("No hay publicaciones ambientales disponibles todavía.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setTitle("Feed Ambiental");
    void loadHistory();
  }, [loadHistory, setTitle]);

  useEffect(() => {
    if (!publicationId) {
      setSharedPublication(null);
      setSharedError(null);
      setSharedLoading(false);
      return;
    }
    let cancelled = false;
    setSharedLoading(true);
    setSharedError(null);
    void getPublicEnvironmentalPublication(publicationId)
      .then((publication) => {
        if (!cancelled) setSharedPublication(publication);
      })
      .catch(() => {
        if (!cancelled) {
          setSharedPublication(null);
          setSharedError("No se pudo cargar la publicación compartida.");
        }
      })
      .finally(() => {
        if (!cancelled) setSharedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [publicationId]);


  const entries = useMemo(
    () => feeds.flatMap((feed) => feed.response.items.map((item) => ({ feed, item }))),
    [feeds]
  );
  const sharedEntry = useMemo(
    () => sharedPublication
      ? {
        impressionId: sharedPublication.impression_id,
        item: {
          candidate: sharedPublication.candidate,
          position: 0,
          propensity: 1,
        } satisfies FeedItem,
      }
      : null,
    [sharedPublication]
  );
  const contextFilter = searchParams.get("context");
  const items = useMemo(
    () => {
      const savedSignalIds = new Set(loadSavedEnvironmentalSignalIds());
      if (publicationId) return sharedEntry ? [sharedEntry] : [];
      const filteredEntries = entries.filter(({ item }) => {
        const matchesKind = filter === "all" || item.candidate.kind === filter;
        const matchesContext = !contextFilter
          || item.candidate.contexts.some((context) => context.context_id === contextFilter);
        const matchesSaved = !savedOnly || savedSignalIds.has(item.candidate.candidate_id);
        return matchesKind && matchesContext && matchesSaved;
      });
      const visibleEntries = savedOnly
        ? Array.from(new Map(filteredEntries.map((entry) => [entry.item.candidate.candidate_id, entry])).values())
        : filteredEntries;
      return visibleEntries
        .map(({ feed, item }) => ({ impressionId: feed.impression_id, item }));
    },
    [contextFilter, entries, filter, publicationId, savedOnly, sharedEntry]
  );

  const handleContextSelect = (contextId: string) => {
    navigate(`/dashboard?context=${encodeURIComponent(contextId)}`);
  };

  const handleFeedback = async (
    impressionId: string,
    candidateId: string,
    action: FeedbackAction,
    candidate?: FeedCandidate
  ) => {
    setFeedbackError(null);
    try {
      await sendEnvironmentalFeedFeedback(
        impressionId,
        candidateId,
        action,
        candidate ? `feed_kind:${candidate.kind}` : ""
      );
      setFeedback((current) => ({ ...current, [`${impressionId}:${candidateId}`]: action }));
    } catch {
      setFeedbackError("No se pudo registrar el feedback. La publicación puede haber expirado.");
    }
  };

  if (loading || sharedLoading) {
    return <Box className={classes.centerState}><CircularProgress /><Typography>Cargando publicaciones ambientales…</Typography></Box>;
  }

  if (publicationId && !sharedPublication) {
    return <Box className={classes.centerState}><Alert severity="info">{sharedError || "No se pudo cargar la publicación compartida."}</Alert></Box>;
  }

  if (!publicationId && (error || feeds.length === 0)) {
    return <Box className={classes.centerState}><Alert severity="info">{error || "Feed sin publicaciones"}</Alert></Box>;
  }

  if (!publicationId && savedOnly && items.length === 0) {
    return (
      <Box className={classes.centerState}>
        <Alert severity="info">
          No hay señales guardadas en este dispositivo todavía.
        </Alert>
      </Box>
    );
  }

  const latest = feeds[0];
  const latestPolicy = latest?.response.policy_id || "Publicación compartida";
  const kinds = Array.from(new Set(
    publicationId && sharedPublication
      ? [sharedPublication.candidate.kind]
      : entries.map(({ item }) => item.candidate.kind)
  ));
  const activeContextLabel = contextFilter
    ? entries
      .flatMap(({ item }) => item.candidate.contexts)
      .find((context) => context.context_id === contextFilter)?.label || contextFilter
    : null;

  return (
    <Box className={classes.page}>
      {isMobile ? (
        <Box component="header" className={classes.mobileHeader}>
          <Box className={classes.mobileHeaderTop}>
            <Box className={classes.mobileBranding}>
              <PartnerLogos compact label="Powered by" shiftPyxis />
            </Box>
            <Tooltip title={isFullscreen ? "Salir de pantalla completa" : "Ver en pantalla completa"}>
              <IconButton
                className={classes.mobileFullscreenButton}
                onClick={() => void toggleFullscreen()}
                aria-label={isFullscreen ? "Salir de pantalla completa" : "Ver en pantalla completa"}
              >
                {isFullscreen ? <FullscreenExitRoundedIcon /> : <FullscreenRoundedIcon />}
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="overline" className={classes.eyebrow}>
            Protea · Feed ambiental
          </Typography>
          <Typography variant="h4" className={classes.mobileHeaderTitle}>
            Actualidad ambiental
          </Typography>
          <Typography variant="body2" className={classes.mobileHeaderText}>
            {entries.length} señales · datos, frescura y procedencia visibles
          </Typography>
          <Typography variant="body2" className={classes.mobileMethodNote}>
            Powered by BeadCompass · Genera estas publicaciones a partir de datos ambientales mediante reflexión guiada.
          </Typography>
          <Stack direction="row" gap={0.75} alignItems="center" className={classes.mobilePolicy}>
            <QueryStatsOutlinedIcon fontSize="small" />
            <Typography variant="caption">{latestPolicy}</Typography>
          </Stack>
        </Box>
      ) : (
        <Box className={classes.hero}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={3}>
            <Box>
              <Box className={classes.brand}>
                <PartnerLogos label="Powered by" shiftPyxis />
              </Box>
              <Typography variant="overline" className={classes.eyebrow}>PROTEA · FEED AMBIENTAL</Typography>
              <Typography variant="h3" className={classes.heroTitle}>Lo que está pasando en nuestros ecosistemas</Typography>
              <Typography className={classes.heroText}>
                Publicaciones contextualizadas con mediciones, histórico, frescura y procedencia visibles.
              </Typography>
              <Typography className={classes.heroMethod}>
                Powered by BeadCompass · Genera estas publicaciones a partir de datos ambientales mediante reflexión guiada.
              </Typography>
            </Box>
            <Stack direction="row" gap={1} alignItems="center">
              <Stack direction="row" gap={1} alignItems="center" className={classes.policyBadge}>
                <QueryStatsOutlinedIcon />
                <Box>
                  <Typography variant="caption">Policy activa</Typography>
                  <Typography fontWeight={700}>{latestPolicy}</Typography>
                </Box>
              </Stack>
              <Tooltip title={isFullscreen ? "Salir de pantalla completa" : "Ver en pantalla completa"}>
                <IconButton
                  className={classes.fullscreenButton}
                  onClick={() => void toggleFullscreen()}
                  aria-label={isFullscreen ? "Salir de pantalla completa" : "Ver en pantalla completa"}
                >
                  {isFullscreen ? <FullscreenExitRoundedIcon /> : <FullscreenRoundedIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      )}
      {fullscreenError && (
        <Alert severity="info" onClose={() => setFullscreenError(false)}>
          La pantalla completa no está disponible en este navegador.
        </Alert>
      )}

      <Stack direction="row" gap={1} flexWrap="wrap" className={classes.filters}>
        <Chip label="Todo" onClick={() => setFilter("all")} color={filter === "all" ? "primary" : "default"} />
        {savedOnly && (
          <Chip
            label="Señales guardadas"
            color="secondary"
            onDelete={() => navigate("/environmental-feed")}
          />
        )}
        {kinds.map((kind) => (
          <Chip key={kind} label={kindLabels[kind] || kind} onClick={() => setFilter(kind)} color={filter === kind ? "primary" : "default"} />
        ))}
        {activeContextLabel && (
          <Chip
            label={`Estación: ${activeContextLabel}`}
            color="secondary"
            onClick={() => handleContextSelect(contextFilter || "")}
            onDelete={() => handleContextSelect(contextFilter || "")}
          />
        )}
      </Stack>

      {publicationId && sharedPublication && (
        <Alert severity="info">
          Publicación compartida · podés explorar este contenido con un acceso anónimo temporal.
        </Alert>
      )}
      {feedbackError && <Alert severity="warning" onClose={() => setFeedbackError(null)}>{feedbackError}</Alert>}
      <Box className={classes.feedColumn}>
        {items.map(({ impressionId, item }) => (
          <FeedCard
            key={`${impressionId}:${item.candidate.candidate_id}`}
            impressionId={impressionId}
            item={item}
            history={feeds}
            submittedAction={feedback[`${impressionId}:${item.candidate.candidate_id}`]}
            onFeedback={(candidateId, action) => handleFeedback(impressionId, candidateId, action, item.candidate)}
            onContextSelect={handleContextSelect}
            mobile={isMobile}
          />
        ))}
      </Box>
      <Box className={classes.historySentinel} aria-live="polite">
        <Typography variant="caption">
          {publicationId ? "Esta publicación llegó desde un enlace compartido." : "Todas las publicaciones ambientales fueron cargadas."}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" className={classes.footerNote}>
        {items.length} publicaciones cargadas · {feeds.length} feeds generados · {latest?.response.feature_schema_version || "enlace compartido"} · Las señales históricas no sustituyen criterios oficiales de aptitud para baño.
      </Typography>
    </Box>
  );
}
